/* ============================================================
   Model Request form  (sections/model-request-form.liquid)

   - Photos are downscaled in the browser (canvas -> JPEG) so a 6-photo
     submission stays a few MB instead of 60.
   - Everything posts as one multipart request to <endpoint>/api/submit.
     No custom headers, so the browser sends it without a CORS preflight.
   - Cloudflare Turnstile is optional; it renders only when the section has
     a site key.
   ============================================================ */
(function () {
  'use strict';

  var SELECTOR = '[data-section-type="model-request-form"]';
  var JPEG_QUALITY = 0.86;
  var UPLOAD_TIMEOUT_MS = 600000;
  var MB = 1024 * 1024;

  // ------------------------------------------------------------
  // Turnstile plumbing. The api.js script is loaded with
  // ?onload=mrfTurnstileOnload; whichever of (our script, their script)
  // lands second does the render.
  // ------------------------------------------------------------
  var turnstileQueue = [];

  window.mrfTurnstileOnload = function () {
    var queue = turnstileQueue.slice();
    turnstileQueue = [];
    queue.forEach(renderTurnstile);
  };

  function renderTurnstile(inst) {
    if (!inst.turnstileEl || inst.turnstileId !== null) return;
    if (!window.turnstile || typeof window.turnstile.render !== 'function') {
      if (turnstileQueue.indexOf(inst) === -1) turnstileQueue.push(inst);
      return;
    }
    try {
      inst.turnstileId = window.turnstile.render(inst.turnstileEl, {
        sitekey: inst.turnstileKey,
        theme: 'light',
        'refresh-expired': 'auto'
      });
    } catch (e) {
      inst.turnstileId = null;
    }
  }

  function turnstileToken(inst) {
    if (!inst.turnstileEl) return null; // not configured on this section
    if (inst.turnstileId === null || !window.turnstile) return '';
    try { return window.turnstile.getResponse(inst.turnstileId) || ''; } catch (e) { return ''; }
  }

  function turnstileReset(inst) {
    if (inst.turnstileId === null || !window.turnstile) return;
    try { window.turnstile.reset(inst.turnstileId); } catch (e) { /* noop */ }
  }

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function toInt(v, fallback) { var n = parseInt(v, 10); return isNaN(n) ? fallback : n; }
  function uid() { return 'p' + Math.random().toString(36).slice(2, 10); }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function formatMb(bytes) { return (bytes / (1024 * 1024)).toFixed(1) + ' MB'; }

  function track(name, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data || {}));
    } catch (e) { /* noop */ }
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') window.posthog.capture(name, data || {});
    } catch (e) { /* noop */ }
  }

  function isImageFile(file) {
    if (file.type && file.type.indexOf('image/') === 0) return true;
    return /\.(heic|heif|jpe?g|png|webp)$/i.test(file.name || '');
  }

  function baseName(name) {
    return String(name || 'photo').replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '_').slice(0, 60) || 'photo';
  }

  // ------------------------------------------------------------
  // Image decode + downscale
  // ------------------------------------------------------------
  function loadViaImg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
  }

  function loadImage(file) {
    if (typeof window.createImageBitmap === 'function') {
      return window.createImageBitmap(file, { imageOrientation: 'from-image' })
        .catch(function () { return loadViaImg(file); });
    }
    return loadViaImg(file);
  }

  // HEIC/HEIF decoding for browsers without native support (Chrome, Firefox).
  // Safari decodes HEIC itself, so the ~3 MB converter loads only on demand.
  var HEIC_TO_URL = 'https://cdn.jsdelivr.net/npm/heic-to@1.6.5/dist/iife/heic-to.js';
  var heicToPromise = null;

  function isHeic(file) {
    return /^image\/hei[cf]/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');
  }

  function loadHeicTo() {
    if (window.HeicTo) return Promise.resolve(window.HeicTo);
    if (!heicToPromise) {
      heicToPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = HEIC_TO_URL;
        s.async = true;
        s.onload = function () { window.HeicTo ? resolve(window.HeicTo) : reject(new Error('heic-to')); };
        s.onerror = function () { reject(new Error('heic-to')); };
        document.head.appendChild(s);
      }).catch(function (err) { heicToPromise = null; throw err; });
    }
    return heicToPromise;
  }

  function heicToJpeg(file) {
    return loadHeicTo().then(function (heicTo) {
      return heicTo({ blob: file, type: 'image/jpeg', quality: JPEG_QUALITY });
    });
  }

  // Resolves to a Blob (JPEG) no larger than maxEdge on its longest side.
  // HEIC the browser cannot decode is converted to JPEG first; anything still
  // undecodable rejects, and the caller sends the original file as-is.
  function downscale(file, maxEdge) {
    return loadImage(file).catch(function (err) {
      if (!isHeic(file)) throw err;
      return heicToJpeg(file).then(function (jpeg) {
        file = jpeg; // so the small-JPEG shortcut below can apply
        return loadImage(jpeg);
      });
    }).then(function (img) {
      var w = img.naturalWidth || img.width;
      var h = img.naturalHeight || img.height;
      if (!w || !h) throw new Error('decode');

      var scale = Math.min(1, maxEdge / Math.max(w, h));
      var alreadySmallJpeg = scale === 1 && file.type === 'image/jpeg' && file.size < 1.5 * 1024 * 1024;
      if (alreadySmallJpeg) {
        if (img.close) img.close();
        return file;
      }

      var cw = Math.max(1, Math.round(w * scale));
      var ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement('canvas');
      canvas.width = cw;
      canvas.height = ch;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(img, 0, 0, cw, ch);
      if (img.close) img.close();

      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (blob) resolve(blob); else reject(new Error('encode'));
        }, 'image/jpeg', JPEG_QUALITY);
      });
    });
  }

  // ------------------------------------------------------------
  // Instance
  // ------------------------------------------------------------
  function init(root) {
    if (!root || root.__mrf) return;
    var form = qs(root, 'form');
    if (!form) return;

    var inst = {
      root: root,
      form: form,
      endpoint: (root.getAttribute('data-endpoint') || '').replace(/\/+$/, ''),
      maxPhotos: toInt(root.getAttribute('data-max-photos'), 5),
      maxBytes: toInt(root.getAttribute('data-max-photo-mb'), 15) * MB,
      maxPdfBytes: toInt(root.getAttribute('data-max-pdf-mb'), 20) * MB,
      maxVideoBytes: toInt(root.getAttribute('data-max-video-mb'), 30) * MB,
      attachments: { portfolio_pdf: null, video: null },
      maxEdge: toInt(root.getAttribute('data-max-edge'), 2000),
      turnstileKey: root.getAttribute('data-turnstile-key') || '',
      turnstileEl: qs(root, '[data-turnstile]'),
      turnstileId: null,
      photos: [],
      busy: false,
      status: qs(root, '.model-request__status'),
      success: qs(root, '[data-success]'),
      submit: qs(root, '[data-submit]'),
      progress: qs(root, '[data-progress]'),
      progressBar: qs(root, '[data-progress-bar]'),
      progressText: qs(root, '[data-progress-text]'),
      previews: qs(root, '[data-previews]'),
      dropzone: qs(root, '[data-dropzone]'),
      fileInput: qs(root, 'input[type="file"]'),
      countrySelect: qs(root, 'select[name="country"]'),
      stateWrap: qs(root, '.model-request__state')
    };
    root.__mrf = inst;

    bindPhotos(inst);
    bindAttachments(inst);
    bindCountry(inst);
    form.addEventListener('submit', function (e) { e.preventDefault(); onSubmit(inst); });

    // Clear a field's error as soon as it changes.
    form.addEventListener('input', function (e) { if (e.target && e.target.name) clearError(inst, e.target.name); });
    form.addEventListener('change', function (e) { if (e.target && e.target.name) clearError(inst, e.target.name); });

    if (inst.turnstileEl && inst.turnstileKey) renderTurnstile(inst);
  }

  // ------------------------------------------------------------
  // Country -> State/Province
  // ------------------------------------------------------------
  function bindCountry(inst) {
    var select = inst.countrySelect;
    if (!select || !inst.stateWrap) return;

    if (!select.value) {
      var def = select.getAttribute('data-default');
      if (def) select.value = def;
    }

    select.addEventListener('change', function () { syncState(inst); });
    syncState(inst);
  }

  function syncState(inst) {
    var select = inst.countrySelect;
    var option = select.options[select.selectedIndex];
    var provinces = [];
    if (option) {
      try { provinces = JSON.parse(option.getAttribute('data-provinces') || '[]'); } catch (e) { provinces = []; }
    }

    var current = qs(inst.stateWrap, '[name="state"]');
    var currentValue = current ? current.value : '';
    var id = current ? current.id : 'mrf-state';
    var el;

    if (provinces && provinces.length) {
      if (current && current.tagName === 'SELECT' && current.getAttribute('data-country') === select.value) return;
      el = document.createElement('select');
      el.innerHTML = '<option value="">Select</option>' + provinces.map(function (p) {
        var name = Array.isArray(p) ? p[0] : p;
        return '<option value="' + escapeHtml(name) + '">' + escapeHtml(name) + '</option>';
      }).join('');
      el.setAttribute('data-country', select.value);
      if (currentValue) el.value = currentValue;
    } else {
      if (current && current.tagName === 'INPUT') return;
      el = document.createElement('input');
      el.type = 'text';
      el.maxLength = 80;
      el.setAttribute('autocomplete', 'address-level1');
    }

    el.id = id;
    el.name = 'state';
    el.className = 'input-full';
    el.required = true;
    inst.stateWrap.innerHTML = '';
    inst.stateWrap.appendChild(el);
  }

  // ------------------------------------------------------------
  // Photos
  // ------------------------------------------------------------
  function bindPhotos(inst) {
    var input = inst.fileInput;
    var zone = inst.dropzone;
    if (!input || !zone) return;

    input.addEventListener('change', function () {
      addFiles(inst, input.files);
      input.value = ''; // so re-picking the same file fires change again
    });

    ['dragenter', 'dragover'].forEach(function (evt) {
      zone.addEventListener(evt, function (e) {
        e.preventDefault();
        e.stopPropagation();
        zone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'dragend'].forEach(function (evt) {
      zone.addEventListener(evt, function (e) {
        e.preventDefault();
        zone.classList.remove('is-dragover');
      });
    });
    zone.addEventListener('drop', function (e) {
      e.preventDefault();
      e.stopPropagation();
      zone.classList.remove('is-dragover');
      if (e.dataTransfer && e.dataTransfer.files) addFiles(inst, e.dataTransfer.files);
    });

    inst.previews.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-remove]');
      if (!btn) return;
      removePhoto(inst, btn.getAttribute('data-remove'));
    });
  }

  function addFiles(inst, fileList) {
    if (inst.busy) return;
    var files = Array.prototype.slice.call(fileList || []);
    if (!files.length) return;
    clearError(inst, 'photos');

    var problems = [];
    files.forEach(function (file) {
      if (inst.photos.length >= inst.maxPhotos) {
        problems.push('You can add up to ' + inst.maxPhotos + ' photos.');
        return;
      }
      if (!isImageFile(file)) {
        problems.push(escapeHtml(file.name) + ' is not an image.');
        return;
      }
      if (file.size > inst.maxBytes) {
        problems.push(escapeHtml(file.name) + ' is ' + formatMb(file.size) + ', the limit is ' + formatMb(inst.maxBytes) + '.');
        return;
      }

      var item = { id: uid(), name: baseName(file.name) + '.jpg', original: file, blob: null, url: '', state: 'processing' };
      inst.photos.push(item);
      renderPreviews(inst);

      downscale(file, inst.maxEdge).then(function (blob) {
        if (inst.photos.indexOf(item) === -1) return; // removed while processing
        item.blob = blob;
        item.name = blob === file ? (file.name || item.name) : item.name;
        item.url = URL.createObjectURL(blob);
        item.state = 'ready';
        renderPreviews(inst);
      }).catch(function () {
        if (inst.photos.indexOf(item) === -1) return;
        // Could not decode in this browser (typically HEIC on Chrome). Send the original as-is.
        item.blob = file;
        item.name = file.name || item.name;
        item.url = '';
        item.state = 'ready';
        renderPreviews(inst);
      });
    });

    if (problems.length) {
      problems = problems.filter(function (p, i) { return problems.indexOf(p) === i; });
      setError(inst, 'photos', problems.join(' '));
    }
  }

  function removePhoto(inst, id) {
    if (inst.busy) return;
    inst.photos = inst.photos.filter(function (p) {
      if (p.id !== id) return true;
      if (p.url) URL.revokeObjectURL(p.url);
      return false;
    });
    renderPreviews(inst);
  }

  function renderPreviews(inst) {
    inst.previews.innerHTML = inst.photos.map(function (p) {
      var img = p.url
        ? '<img class="model-request__preview-img" src="' + p.url + '" alt="">'
        : '<div class="model-request__preview-img" aria-hidden="true"></div>';
      var state = p.state === 'processing'
        ? '<span class="model-request__preview-state">Preparing</span>'
        : (p.url ? '' : '<span class="model-request__preview-state">No preview</span>');
      return '<li class="model-request__preview' + (p.state === 'processing' ? ' is-processing' : '') + '">' +
        img + state +
        '<button type="button" class="model-request__remove" data-remove="' + p.id + '" aria-label="Remove ' + escapeHtml(p.name) + '">&times;</button>' +
        '<span class="model-request__preview-name">' + escapeHtml(p.name) + '</span>' +
        '</li>';
    }).join('');
  }

  // ------------------------------------------------------------
  // Single-file attachments: PDF portfolio and video
  // ------------------------------------------------------------
  var ATTACH_RULES = {
    portfolio_pdf: {
      accept: function (file) { return file.type === 'application/pdf' || /\.pdf$/i.test(file.name || ''); },
      wrong: 'The portfolio must be a PDF file.',
      max: function (inst) { return inst.maxPdfBytes; }
    },
    video: {
      accept: function (file) { return (file.type && file.type.indexOf('video/') === 0) || /\.(mp4|mov|m4v|webm)$/i.test(file.name || ''); },
      wrong: 'The video must be an MP4, MOV or WebM file.',
      max: function (inst) { return inst.maxVideoBytes; }
    }
  };

  function bindAttachments(inst) {
    qsa(inst.root, '[data-attach]').forEach(function (box) {
      var name = box.getAttribute('data-attach');
      var input = qs(box, 'input[type="file"]');
      var remove = qs(box, '[data-attach-remove]');
      if (!input || !ATTACH_RULES[name]) return;

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        input.value = '';
        if (file) setAttachment(inst, name, file);
      });
      if (remove) {
        remove.addEventListener('click', function () {
          if (inst.busy) return;
          inst.attachments[name] = null;
          renderAttachment(inst, name);
          clearError(inst, name);
        });
      }
      renderAttachment(inst, name);
    });
  }

  function setAttachment(inst, name, file) {
    if (inst.busy) return;
    var rule = ATTACH_RULES[name];
    clearError(inst, name);
    if (!rule.accept(file)) { setError(inst, name, rule.wrong); return; }
    var max = rule.max(inst);
    if (file.size > max) {
      setError(inst, name, escapeHtml(file.name) + ' is ' + formatMb(file.size) + ', the limit is ' + Math.round(max / MB) + ' MB.');
      return;
    }
    inst.attachments[name] = file;
    renderAttachment(inst, name);
  }

  function renderAttachment(inst, name) {
    var box = qs(inst.root, '[data-attach="' + name + '"]');
    if (!box) return;
    var file = inst.attachments[name];
    var chip = qs(box, '[data-attach-chip]');
    var button = qs(box, '[data-attach-button]');
    var nameEl = qs(box, '[data-attach-name]');
    var sizeEl = qs(box, '[data-attach-size]');
    if (chip) chip.hidden = !file;
    if (button) button.hidden = !!file;
    if (nameEl) nameEl.textContent = file ? file.name : '';
    if (sizeEl) sizeEl.textContent = file ? formatMb(file.size) : '';
  }

  // ------------------------------------------------------------
  // Errors
  // ------------------------------------------------------------
  function fieldEl(inst, name) {
    return qs(inst.form, '[name="' + name + '"]');
  }

  function errorEl(inst, name, create) {
    var existing = qs(inst.form, '[data-error-for="' + name + '"]');
    if (existing || !create) return existing;
    var field = fieldEl(inst, name);
    if (!field) return null;
    var p = document.createElement('p');
    p.className = 'model-request__error';
    p.setAttribute('data-error-for', name);
    var anchor = field.closest('.model-request__prefixed') || field.closest('.model-request__state') || field;
    anchor.parentNode.insertBefore(p, anchor.nextSibling);
    return p;
  }

  function setError(inst, name, message) {
    var p = errorEl(inst, name, true);
    if (p) { p.innerHTML = message; p.hidden = false; }
    var field = fieldEl(inst, name);
    if (field && field.type !== 'file' && field.type !== 'checkbox') {
      field.classList.add('is-invalid');
      field.setAttribute('aria-invalid', 'true');
    }
  }

  function clearError(inst, name) {
    var p = errorEl(inst, name, false);
    if (p) { p.hidden = true; p.innerHTML = ''; }
    var field = fieldEl(inst, name);
    if (field) { field.classList.remove('is-invalid'); field.removeAttribute('aria-invalid'); }
  }

  function clearAllErrors(inst) {
    qsa(inst.form, '[data-error-for]').forEach(function (p) { p.hidden = true; p.innerHTML = ''; });
    qsa(inst.form, '.is-invalid').forEach(function (f) { f.classList.remove('is-invalid'); f.removeAttribute('aria-invalid'); });
    showStatus(inst, '');
  }

  function showStatus(inst, message) {
    if (!inst.status) return;
    inst.status.innerHTML = message || '';
    inst.status.hidden = !message;
  }

  // ------------------------------------------------------------
  // Validation
  // ------------------------------------------------------------
  function normalizeInstagram(raw) {
    var v = String(raw || '').trim();
    v = v.replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, '').replace(/[/?#].*$/, '');
    v = v.replace(/^@+/, '');
    return v;
  }

  function validate(inst) {
    var errors = []; // [name, message]
    var form = inst.form;

    qsa(form, '[required]').forEach(function (field) {
      var name = field.name;
      if (!name || field.type === 'checkbox') return;
      if (name === 'website') return;
      var value = String(field.value || '').trim();
      if (!value) errors.push([name, 'Required.']);
    });

    var email = fieldEl(inst, 'email');
    if (email && email.value.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.value.trim())) {
      errors.push(['email', 'Enter a valid email address.']);
    }

    var age = fieldEl(inst, 'age');
    if (age && age.value.trim()) {
      var n = toInt(age.value, 0);
      if (n < 13 || n > 99) errors.push(['age', 'Enter an age between 13 and 99.']);
    }

    var ig = fieldEl(inst, 'instagram');
    if (ig && ig.value.trim()) {
      var handle = normalizeInstagram(ig.value);
      if (!/^[A-Za-z0-9._]{1,30}$/.test(handle)) errors.push(['instagram', 'Enter just your handle, like nermal.']);
      else ig.value = handle;
    }

    var url = fieldEl(inst, 'portfolio_url');
    if (url && url.value.trim()) {
      var v = url.value.trim();
      if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
      var ok = false;
      try { var u = new URL(v); ok = /^https?:$/.test(u.protocol) && u.hostname.indexOf('.') > 0; } catch (e) { ok = false; }
      if (!ok) errors.push(['portfolio_url', 'Enter a full link, like https://yoursite.com.']);
      else url.value = v;
    }

    var ready = inst.photos.filter(function (p) { return p.state === 'ready'; }).length;
    var processing = inst.photos.length - ready;
    if (processing > 0) errors.push(['photos', 'Your photos are still being prepared. Give it a second and try again.']);
    else if (!inst.photos.length && !inst.attachments.portfolio_pdf) {
      errors.push(['photos', 'Add at least one photo or a PDF portfolio.']);
    }

    var consent = fieldEl(inst, 'consent');
    if (consent && !consent.checked) errors.push(['consent', 'Please confirm to continue.']);

    var token = turnstileToken(inst);
    if (token === '') errors.push(['turnstile', 'Please complete the verification below.']);

    return errors;
  }

  // ------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------
  function setBusy(inst, busy) {
    inst.busy = busy;
    if (inst.submit) inst.submit.disabled = busy;
    qsa(inst.form, 'input, select, textarea, button').forEach(function (el) {
      if (el === inst.submit) return;
      el.disabled = busy;
    });
    if (inst.progress) inst.progress.hidden = !busy;
    setProgress(inst, 0);
  }

  function setProgress(inst, ratio) {
    if (inst.progressBar) inst.progressBar.style.setProperty('--mrf-progress', Math.round(ratio * 100) + '%');
    if (inst.progressText) {
      inst.progressText.textContent = ratio >= 1 ? 'Saving your submission' : 'Uploading ' + Math.round(ratio * 100) + '%';
    }
  }

  function onSubmit(inst) {
    if (inst.busy) return;
    clearAllErrors(inst);

    if (!inst.endpoint) {
      showStatus(inst, 'This form is not set up yet. Please try again later or email info@ripndipclothing.com.');
      if (inst.status) { try { inst.status.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* noop */ } }
      return;
    }

    var errors = validate(inst);
    if (errors.length) {
      var first = null;
      errors.forEach(function (err) {
        if (err[0] === 'turnstile') { showStatus(inst, err[1]); return; }
        setError(inst, err[0], err[1]);
        if (!first) first = err[0];
      });
      if (!first && errors.length) first = null;
      var target = first ? (fieldEl(inst, first) || errorEl(inst, first, false)) : inst.turnstileEl;
      if (target) {
        try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* noop */ }
        if (target.focus && first !== 'photos') { try { target.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
      }
      return;
    }

    var data = new FormData(inst.form);
    data.delete('photos');
    data.delete('portfolio_pdf');
    data.delete('video');
    inst.photos.forEach(function (p) { data.append('photos', p.blob, p.name); });
    if (inst.attachments.portfolio_pdf) data.append('portfolio_pdf', inst.attachments.portfolio_pdf, inst.attachments.portfolio_pdf.name);
    if (inst.attachments.video) data.append('video', inst.attachments.video, inst.attachments.video.name);
    data.append('page_url', window.location.href);

    setBusy(inst, true);

    var xhr = new XMLHttpRequest();
    xhr.open('POST', inst.endpoint + '/api/submit', true);
    xhr.timeout = UPLOAD_TIMEOUT_MS;
    xhr.responseType = 'json';

    if (xhr.upload) {
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable) setProgress(inst, e.loaded / e.total);
      };
      xhr.upload.onload = function () { setProgress(inst, 1); };
    }

    xhr.onload = function () {
      var body = xhr.response;
      if (typeof body === 'string') { try { body = JSON.parse(body); } catch (e) { body = null; } }
      if (xhr.status >= 200 && xhr.status < 300 && body && body.ok) {
        onSuccess(inst, body);
        return;
      }
      onFailure(inst, body, xhr.status);
    };
    xhr.onerror = function () { onFailure(inst, null, 0); };
    xhr.ontimeout = function () { onFailure(inst, { error: 'The upload timed out. Check your connection and try again.' }, 0); };

    xhr.send(data);
  }

  function onSuccess(inst, body) {
    setBusy(inst, false);
    inst.form.hidden = true;
    if (inst.success) {
      inst.success.hidden = false;
      try { inst.success.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* noop */ }
      try { inst.success.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
    inst.photos.forEach(function (p) { if (p.url) URL.revokeObjectURL(p.url); });
    inst.photos = [];
    inst.attachments = { portfolio_pdf: null, video: null };
    track('model_request_submitted', { submission_id: body && body.id ? body.id : undefined });
  }

  function onFailure(inst, body, status) {
    setBusy(inst, false);
    turnstileReset(inst);

    var fields = body && body.fields;
    var first = null;
    if (fields && typeof fields === 'object') {
      Object.keys(fields).forEach(function (name) {
        setError(inst, name, escapeHtml(fields[name]));
        if (!first) first = name;
      });
    }

    var message = (body && body.error) ? escapeHtml(body.error) : null;
    if (!message) {
      message = status === 0
        ? 'We could not reach the server. Check your connection and try again.'
        : 'Something went wrong on our end. Please try again in a minute.';
    }
    showStatus(inst, message);

    var target = first ? fieldEl(inst, first) : inst.status;
    if (target) { try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* noop */ } }
    track('model_request_failed', { status: status });
  }

  // ------------------------------------------------------------
  // Boot
  // ------------------------------------------------------------
  function boot() { qsa(document, SELECTOR).forEach(init); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && (e.target.matches && e.target.matches(SELECTOR) ? e.target : qs(e.target, SELECTOR));
    if (root) init(root);
  });
})();
